import { useEffect } from "react";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ImagesSlider from "../components/ImagesSlider";
import { currencyFormat } from "../shared/helpers/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faViber, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useContext } from "react";
import AuthContext from "../shared/contexs/auth-contex";
import { faFile, faFilePdf, faShareAltSquare, faArrowLeft, faFileCircleExclamation, faPlay } from "@fortawesome/free-solid-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import logo from "../assets/images/logo.png";

const ProductDetailsPage = () => {

  let { prdId } = useParams();
  const [productDetails, setProductDetails] = useState({});
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const waMessage = () => {
    const url = `https://wa.me/+381698003128?text=${encodeURIComponent('Poštovani, Interesuje me ' + (productDetails?.manufacturer_name ?? '') + ' ' + (productDetails?.model_name ?? '') + (productDetails?.tablica_name ? (' (' + productDetails?.tablica_name + ')') : '') + ' iz Vaše ponude. Hvala')}`;
    window.open(url, '_blank');
  };

  const viberMessage = () => {
    const url = `viber://chat?number=+381698003128&draft=${'Poštovani, Interesuje me ' + (productDetails?.manufacturer_name ?? '') + ' ' + (productDetails?.model_name ?? '') + (productDetails?.tablica_name ? (' (' + productDetails?.tablica_name + ')') : '') + ' iz Vaše ponude. Hvala'}`;
    window.open(url, '_blank');
  };

  const getProductDetils = async (id) => {
    const currentTime = new Date(new Date().getTime());
    localStorage.setItem('lastHttp', currentTime);
    try {
      const response = await fetch(
        "https://api.bagi.rs/api/v1/get/vehicle",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${user?.access_token}`
          },
          body: JSON.stringify(
            {
              vehicle_id: id
            }
          )
        }
      );

      if (!response.ok) {
        throw new Error("Error updating data");
      }

      let updatedData = await response.json();
      setProductDetails(updatedData?.data?.vehicle ?? {});
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    getProductDetils(prdId);
  }, [prdId, user?.access_token]);

  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(
        "https://pdfclown.files.wordpress.com/2011/04/texthighlight.jpg"
      );
      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
  
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
  
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        updateMetaTags(productDetails);
        await navigator.share({
          title: (productDetails?.manufacturer_name ?? '') + ' ' + (productDetails?.model_name ?? ''),
          text: '',
          url: window.location.href,
        });
      } else {
        throw new Error('Web Share API nije podržan.');
      }
    } catch (error) {
      console.error('Greška pri deljenju:', error.message);
    }
  };

  const updateMetaTags = (productDetails) => {
    // Update meta tags dynamically based on product details
    const ogTitle = `${productDetails?.manufacturer_name ?? ''} ${productDetails?.model_name ?? ''}`;
    const ogDescription = `${productDetails?.description ?? ''}`;
    const ogImage = productDetails?.images?.[0]?.image_url ?? logo;
    const ogUrl = window.location.href;

    // Remove existing meta tags
    removeMetaTags();

    // Create and append new meta tags
    createMetaTag('og:title', ogTitle);
    createMetaTag('og:description', ogDescription);
    createMetaTag('og:image', ogImage);
    createMetaTag('og:url', ogUrl);
  };

  const createMetaTag = (property, content) => {
    const metaTag = document.createElement('meta');
    metaTag.setAttribute('property', property);
    metaTag.setAttribute('content', content);
    document.head.appendChild(metaTag);
  };

  const removeMetaTags = () => {
    const existingMetaTags = document.querySelectorAll('meta[property^="og:"]');
    existingMetaTags.forEach(tag => tag.remove());
  };

  // SEO optimization function
  const generateSEOData = (product) => {
    const manufacturer = product?.manufacturer_name || '';
    const model = product?.model_name || '';
    const category = product?.category_name || '';
    const year = product?.production_year_c || '';
    const price = product?.price || '';
    const workingHours = product?.machine_working_hours_c || '';
    
    // Generate Serbian SEO-optimized title
    const title = `${manufacturer} ${model} ${year ? year + '. godište' : ''} - ${category} | Bagi Trans`.trim();
    
    // Generate Serbian description with keywords
    const description = `${manufacturer} ${model} ${category.toLowerCase()} ${year ? 'iz ' + year + '. godine' : ''} na prodaju u Srbiji. ${workingHours ? 'Radni sati: ' + workingHours + '.' : ''} ${price && product?.show_price ? 'Cena: ' + currencyFormat(price) + ' €.' : 'Kontaktirajte nas za cenu.'} Uvoz i prodaja građevinskih mašina, bagera, utovarivača. Bagi Trans - vaš partner za kvalitetne mašine.`.substring(0, 160);
    
    // Generate keywords for Serbian search
    const keywords = [
      manufacturer.toLowerCase(),
      model.toLowerCase(),
      category.toLowerCase(),
      'srbija',
      'prodaja',
      'mašine',
      'građevinske mašine',
      'bageri',
      'utovarivači',
      'polovne mašine',
      'uvoz mašina',
      'bagi trans',
      year ? year + ' godište' : '',
      workingHours ? 'radni sati' : '',
      'kvalitetne mašine'
    ].filter(Boolean).join(', ');

    return { title, description, keywords };
  };

  // Generate structured data for Google
  const generateStructuredData = (product) => {
    const structuredData = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": `${product?.manufacturer_name || ''} ${product?.model_name || ''}`.trim(),
      "description": product?.description || `${product?.manufacturer_name || ''} ${product?.model_name || ''} - ${product?.category_name || ''}`,
      "brand": {
        "@type": "Brand",
        "name": product?.manufacturer_name || ''
      },
      "category": product?.category_name || '',
      "productionDate": product?.production_year_c || '',
      "additionalProperty": [
        product?.machine_working_hours_c && {
          "@type": "PropertyValue",
          "name": "Radni sati",
          "value": product.machine_working_hours_c
        },
        product?.machine_engine_power_c && {
          "@type": "PropertyValue", 
          "name": "Snaga motora",
          "value": product.machine_engine_power_c + " KW"
        },
        product?.machine_weight_c && {
          "@type": "PropertyValue",
          "name": "Težina",
          "value": product.machine_weight_c
        }
      ].filter(Boolean),
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "EUR",
        "seller": {
          "@type": "Organization",
          "name": "Bagi Trans",
          "url": "https://bagi.rs",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "RS",
            "addressLocality": "Srbija"
          }
        }
      },
      "image": product?.images?.[0]?.image_url || logo,
      "url": window.location.href
    };

    if (product?.price && product?.show_price) {
      structuredData.offers.price = product.price;
    }

    return JSON.stringify(structuredData);
  };

  const seoData = generateSEOData(productDetails);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Bagi Trans" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={productDetails?.images?.[0]?.image_url || logo} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Bagi Trans - Građevinske mašine" />
        <meta property="og:locale" content="sr_RS" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content={productDetails?.images?.[0]?.image_url || logo} />

        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="RS" />
        <meta name="geo.country" content="Serbia" />
        <meta name="language" content="Serbian" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {generateStructuredData(productDetails)}
        </script>

        {/* Additional Local Business Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Bagi Trans",
            "description": "Uvoz i prodaja građevinskih mašina u Srbiji - bageri, utovarivači, kvalitetne polovne mašine",
            "url": "https://bagi.rs",
            "telephone": "+381698003128",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "RS"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "addressCountry": "RS"
            },
            "openingHours": "Mo-Fr 08:00-17:00",
            "priceRange": "€€",
            "areaServed": {
              "@type": "Country",
              "name": "Serbia"
            },
            "serviceType": "Prodaja građevinskih mašina",
            "knowsAbout": [
              "bageri",
              "utovarivači", 
              "građevinske mašine",
              "polovne mašine",
              "uvoz mašina"
            ]
          })}
        </script>
      </Helmet>
      
      <div className="product-details-page modern-layout">
      {/* Hero Section */}
      <div className="product-hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="back-to-home-section">
              <button 
                className="back-to-home-btn"
                onClick={() => navigate('/')}
                title="Vrati se nazad na mašine"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Vrati se nazad na mašine</span>
              </button>
            </div>
            <h1 className="product-title">
              {(productDetails?.manufacturer_name ?? '') + ' ' + (productDetails?.model_name ?? '')}
            </h1>
          </div>
        </div>
      </div>

      <div className="container product-content">
        <div className="row g-4">
          {/* Image Gallery Section */}
          <div className="col-lg-7">
            <div className="image-gallery-card">
              <ImagesSlider images={productDetails?.images ?? []} />
              
              {/* Social Actions - Modern floating style */}
              <div className="social-actions-modern">
                <div className="social-buttons">
                  <button 
                    className="social-btn whatsapp-btn" 
                    onClick={waMessage}
                    title="Kontaktirajte nas preko WhatsApp"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                    <span>WhatsApp</span>
                  </button>
                  <button 
                    className="social-btn viber-btn" 
                    onClick={viberMessage}
                    title="Kontaktirajte nas preko Viber"
                  >
                    <FontAwesomeIcon icon={faViber} />
                    <span>Viber</span>
                  </button>
                </div>
                {navigator.share && (
                  <button 
                    className="social-btn share-btn" 
                    onClick={handleShare}
                    title="Podelite ovaj proizvod"
                  >
                    <FontAwesomeIcon icon={faShareAltSquare} />
                    <span>Podeli</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Product Details Sidebar */}
          <div className="col-lg-5">
            <div className="product-details-card">
              <div className="details-header">
                <h3>Specifikacije</h3>
              </div>
              
              <div className="details-body">
                <div className="detail-items">
                  <div className="detail-item">
                    <span className="label">Kategorija</span>
                    <span className="value">{productDetails?.category_name ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Cena[EUR]</span>
                    <span className="value price-details-color">
                      {productDetails?.model_name ? ((productDetails?.show_price && productDetails?.price > 0) ? currencyFormat(productDetails?.price ?? 0) + " €" : 'Kontaktirajte nas') : '-'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Proizvođač</span>
                    <span className="value">{productDetails?.manufacturer_name ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Model</span>
                    <span className="value">{productDetails?.model_name ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Godina proizvodnje</span>
                    <span className="value highlight">{productDetails?.production_year_c ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Radni sati</span>
                    <span className="value">{productDetails?.machine_working_hours_c ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Snaga[KW]</span>
                    <span className="value">{productDetails?.machine_engine_power_c ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Zapremina[cm3]</span>
                    <span className="value">{productDetails?.machine_engine_volume_c ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Dimenizija[m](DxŠxV)</span>
                    <span className="value">{productDetails?.dimensions ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Težina</span>
                    <span className="value">{productDetails?.machine_weight_c ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Boja</span>
                    <span className="value">{productDetails?.machine_color_c ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Broj tablice</span>
                    <span className="value">{productDetails?.tablica_name ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Broj šasije</span>
                    <span className={user?.access_token ? "value" : "value restricted"}>
                      {user?.access_token ? (productDetails?.chassis_number_c ?? '-') : 'Samo za registovane korisnike'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Broj motora</span>
                    <span className={user?.access_token ? "value" : "value restricted"}>
                      {user?.access_token ? (productDetails?.machine_engine_code_c ?? '-') : 'Samo za registovane korisnike'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Registarska oznaka</span>
                    <span className={user?.access_token ? "value" : "value restricted"}>
                      {user?.access_token ? (productDetails?.vehicle_registry_code_c ?? '-') : 'Samo za registovane korisnike'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="description-card">
              <div className="details-header">
                <h3>Opis</h3>
              </div>
              <div className="description-content">
                <div 
                  className="description-text"
                  dangerouslySetInnerHTML={{
                    __html: (productDetails?.description ?? '').replace(/\n/g, '<br>')
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Media and Documents Section */}
        <div className="row mt-4 g-4">
          {/* Video Section */}
          <div className="col-12 col-sm-6 video-details">
            <div className="media-card">
              {productDetails?.youtube_video ? (
                <iframe
                  src={`https://www.youtube.com/embed/${productDetails.youtube_video}`}
                  title="Video prezentacija proizvoda"
                  allowFullScreen
                  className="video-iframe"
                ></iframe>
              ) : (
                <div className="no-video-placeholder">
                  <div className="placeholder-content">
                    <FontAwesomeIcon icon={faYoutube} className="youtube-icon" />
                    <h4>Pogledajte naš kanal</h4>
                    <p>Nema specifičnog videa za ovaj proizvod, ali možete pogledati naš YouTube kanal sa više informacija o mašinama.</p>
                    <a 
                      href="https://www.youtube.com/@infobagitrans1623/videos" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="youtube-channel-btn"
                    >
                      <FontAwesomeIcon icon={faPlay} />
                      <span>Idite na kanal</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Documents Section */}
          <div className="col-12 col-sm-6">
            <div className="documents-card">
              <div className="files-details">
                <p>Info dokument</p>
                <div className="documents-list">
                  {productDetails?.documents?.length > 0 ? (
                    productDetails.documents.map((document, index) => (
                      <a
                        key={index}
                        className="document-element-details"
                        href={document?.file_url ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon 
                          icon={document.file_mime_type?.includes('pdf') ? faFilePdf : faFile} 
                          className="me-2 pdf-details" 
                        />
                        {document.file_name ?? ''}
                      </a>
                    ))
                  ) : (
                    <div className="no-documents-placeholder">
                      <FontAwesomeIcon icon={faFileCircleExclamation} className="no-docs-icon" />
                      <p>Trenutno nema dostupnih dokumenata za ovaj proizvod.</p>
                      <small>Za dodatne informacije kontaktirajte nas.</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProductDetailsPage;
